// =============================================================================
// MeuDriver — /pagamento/:chargeId?t=<token>: tela do Pix (Mercado Pago)
//
// Mostra o QR e o "copia e cola" de uma cobrança de payment_charges e
// acompanha o status até o mp-webhook aprovar. Funciona sem login: quem tem o
// link (id + token da cobrança) vê só aquela cobrança — ver a RPC
// get_payment_charge.
// =============================================================================

import { useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import { AlertCircle, Check, Clock, Copy, Loader2, QrCode } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { supabase } from '@/lib/supabase'
import { formatCurrency } from '@/lib/utils'

interface PixCharge {
  id: string
  kind: string
  status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'expired' | 'refunded' | 'charged_back'
  amount: number
  description: string | null
  pix_qr_code: string | null
  pix_qr_code_base64: string | null
  expires_at: string | null
  paid_at: string | null
  return_path: string | null
}

export default function PixPaymentPage() {
  const { chargeId = '' } = useParams()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('t') ?? ''
  const navigate = useNavigate()
  const [now, setNow] = useState(() => Date.now())

  const { data: charge, isLoading } = useQuery<PixCharge | null>({
    queryKey: ['pix-charge', chargeId, token],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_payment_charge', { p_charge_id: chargeId, p_token: token })
      if (error) throw new Error(error.message)
      return (data as PixCharge | null) ?? null
    },
    enabled: Boolean(chargeId && token),
    // Link inválido responde 400; não deixar o aluno ~7 s no spinner (3 retries).
    retry: 1,
    // Pergunta de novo enquanto o Pix estiver em aberto.
    refetchInterval: (query) => (query.state.data?.status === 'pending' ? 4000 : false),
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [])

  // Enquanto o Pix está em aberto, pede ao servidor para conferir no Mercado
  // Pago (reconcile-charge). Não depende só do aviso do MP chegar; a leitura
  // acima (get_payment_charge) mostra o resultado.
  const pendente = charge?.status === 'pending'
  useEffect(() => {
    if (!pendente || !chargeId || !token) return
    const conferir = () => {
      supabase.functions.invoke('reconcile-charge', { body: { charge_id: chargeId, token } }).catch(() => undefined)
    }
    const first = window.setTimeout(conferir, 5000)
    const id = window.setInterval(conferir, 12000)
    return () => { window.clearTimeout(first); window.clearInterval(id) }
  }, [pendente, chargeId, token])

  const expiresAt = charge?.expires_at ? new Date(charge.expires_at).getTime() : null
  const vencido = charge?.status === 'expired' || (charge?.status === 'pending' && expiresAt !== null && expiresAt <= now)

  useEffect(() => {
    if (charge?.status !== 'approved') return
    toast.success('Pagamento confirmado!')
    const destino = charge.return_path ?? '/'
    const id = window.setTimeout(() => navigate(destino, { replace: true }), 1500)
    return () => window.clearTimeout(id)
  }, [charge?.status, charge?.return_path, navigate])

  async function copiar() {
    if (!charge?.pix_qr_code) return
    try {
      await navigator.clipboard.writeText(charge.pix_qr_code)
      toast.success('Código Pix copiado.')
    } catch {
      toast.error('Não foi possível copiar. Selecione o código e copie manualmente.')
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!charge) {
    return (
      <Aviso
        icon={<AlertCircle className="h-10 w-10 text-red-500" />}
        titulo="Cobrança não encontrada"
        texto="O link de pagamento está incompleto ou não é mais válido."
      />
    )
  }

  if (charge.status === 'approved') {
    return (
      <Aviso
        icon={<Check className="h-10 w-10 text-green-600" />}
        titulo="Pagamento confirmado"
        texto="Tudo certo! Estamos te levando de volta…"
        destino={charge.return_path}
      />
    )
  }

  if (vencido || charge.status !== 'pending') {
    return (
      <Aviso
        icon={<Clock className="h-10 w-10 text-amber-500" />}
        titulo={vencido ? 'O Pix venceu' : 'Pagamento não concluído'}
        texto="Este código não pode mais ser pago. Volte e gere um novo pagamento."
        destino={charge.return_path?.split('?')[0]}
      />
    )
  }

  const restante = expiresAt ? Math.max(0, Math.floor((expiresAt - now) / 1000)) : null

  return (
    <div className="max-w-md mx-auto px-4 py-8">
      <Card>
        <CardHeader className="text-center space-y-1">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100 mx-auto">
            <QrCode className="h-6 w-6 text-emerald-700" />
          </div>
          <CardTitle className="text-xl">Pague com Pix</CardTitle>
          {charge.description && <p className="text-sm text-muted-foreground">{charge.description}</p>}
          <p className="text-2xl font-bold">{formatCurrency(Number(charge.amount))}</p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex justify-center">
            {charge.pix_qr_code_base64 ? (
              <img
                src={`data:image/png;base64,${charge.pix_qr_code_base64}`}
                alt="QR Code do Pix"
                className="h-56 w-56"
              />
            ) : charge.pix_qr_code ? (
              <QRCodeSVG value={charge.pix_qr_code} size={224} />
            ) : null}
          </div>

          {charge.pix_qr_code && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Ou use o Pix copia e cola:</p>
              <div className="rounded-md border bg-muted/40 p-2 text-xs break-all font-mono max-h-24 overflow-y-auto">
                {charge.pix_qr_code}
              </div>
              <Button className="w-full" onClick={copiar}>
                <Copy className="h-4 w-4 mr-2" /> Copiar código
              </Button>
            </div>
          )}

          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Aguardando o pagamento
            {restante !== null && (
              <span>· vence em {String(Math.floor(restante / 60)).padStart(2, '0')}:{String(restante % 60).padStart(2, '0')}</span>
            )}
          </div>
          <p className="text-xs text-center text-muted-foreground">
            Abra o app do seu banco, escolha Pix e leia o QR Code ou cole o código. A confirmação aparece aqui sozinha.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

function Aviso({ icon, titulo, texto, destino }: { icon: ReactNode; titulo: string; texto: string; destino?: string | null }) {
  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center space-y-4">
      <div className="flex justify-center">{icon}</div>
      <h1 className="text-xl font-semibold">{titulo}</h1>
      <p className="text-sm text-muted-foreground">{texto}</p>
      <Button asChild variant="outline">
        <Link to={destino || '/'}>Voltar</Link>
      </Button>
    </div>
  )
}
